import { PointerEvent as ReactPointerEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';

type Corner = 'left' | 'right';
type Bounds = { left: number; right: number; top: number; bottom: number };
const viewport = (): Bounds => ({ left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight });

/** Persist the corner, not pixel coordinates, so navigation/menu/viewport changes stay safe. */
export function useArchitectDock(storageKey: string, contentRef?: RefObject<HTMLElement>) {
	const [corner, setCorner] = useState<Corner>(() => {
		try { return localStorage.getItem(storageKey) === 'left' ? 'left' : 'right'; } catch { return 'right'; }
	});
	const [bounds, setBounds] = useState(viewport);
	const [offset, setOffset] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const drag = useRef<{ id: number; x: number; y: number; rect: DOMRect; moved: boolean; offsetX: number }>();
	const suppressClickUntil = useRef(0);
	useEffect(() => {
		const update = () => {
			const rect = contentRef?.current?.getBoundingClientRect();
			const next = rect && rect.width > 0 && rect.height > 0 ? {
				left: Math.max(0, rect.left), right: Math.min(window.innerWidth, rect.right),
				top: Math.max(0, rect.top), bottom: Math.min(window.innerHeight, rect.bottom),
			} : viewport();
			setBounds(previous => previous.left === next.left && previous.right === next.right && previous.top === next.top && previous.bottom === next.bottom ? previous : next);
		};
		update();
		const observer = new ResizeObserver(update);
		if (contentRef?.current) observer.observe(contentRef.current);
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		return () => { observer.disconnect(); window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
	}, [contentRef]);
	const dock = useCallback((next: Corner) => {
		setCorner(next);
		try { localStorage.setItem(storageKey, next); } catch { /* Storage may be disabled. */ }
	}, [storageKey]);
	const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
		if (drag.current || event.button !== 0 || (event.target as HTMLElement).closest('[data-no-drag]')) return;
		const rect = event.currentTarget.getBoundingClientRect();
		drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, rect, moved: false, offsetX: 0 };
	};
	const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
		const current = drag.current;
		if (!current || current.id !== event.pointerId) return;
		const dx = event.clientX - current.x, dy = event.clientY - current.y;
		if (Math.abs(dx) + Math.abs(dy) < 5 && !current.moved) return;
		// Capturing the wrapper on pointer-down retargets the Fab's click to the wrapper.
		// Leave clicks/taps native; capture only once this is actually a drag.
		if (!current.moved) event.currentTarget.setPointerCapture?.(event.pointerId);
		if (!current.moved) setIsDragging(true);
		current.moved = true;
		current.offsetX = Math.max(bounds.left - current.rect.left, Math.min(dx, bounds.right - current.rect.right));
		setOffset({ x: current.offsetX,
			y: Math.max(bounds.top - current.rect.top, Math.min(dy, bounds.bottom - current.rect.bottom)) });
	};
	const finish = (event: ReactPointerEvent<HTMLElement>, cancelled = false) => {
		const current = drag.current;
		if (!current || current.id !== event.pointerId) return;
		if (current.moved) {
			suppressClickUntil.current = Date.now() + 300;
			if (!cancelled) dock(current.rect.left + current.rect.width / 2 + current.offsetX < (bounds.left + bounds.right) / 2 ? 'left' : 'right');
		}
		drag.current = undefined;
		setIsDragging(false);
		setOffset({ x: 0, y: 0 });
		if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
	};
	const inset = Math.min(24, Math.max(8, (bounds.right - bounds.left) / 40));
	return {
		corner, dock, isDragging, canClick: () => Date.now() >= suppressClickUntil.current,
		position: { position: 'fixed' as const, left: corner === 'left' ? bounds.left + inset : undefined,
			right: corner === 'right' ? window.innerWidth - bounds.right + inset : undefined,
			bottom: window.innerHeight - bounds.bottom + inset,
			transform: `translate(${offset.x}px, ${offset.y}px)` },
		// Keep an explicit left anchor in both corners: left/right:auto cannot interpolate.
		bubblePosition: { position: 'fixed' as const,
			left: corner === 'left' ? bounds.left + inset : bounds.right - inset,
			bottom: window.innerHeight - bounds.bottom + inset,
			transform: corner === 'left' ? `translate(${offset.x}px, ${offset.y}px)` : `translate(calc(-100% + ${offset.x}px), ${offset.y}px)` },
		maxWidth: Math.max(0, bounds.right - bounds.left - inset * 2),
		maxHeight: Math.max(0, bounds.bottom - bounds.top - inset * 2),
		dragHandlers: { onPointerDown, onPointerMove, onPointerUp: (event: ReactPointerEvent<HTMLElement>) => finish(event),
			onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => finish(event, true),
			onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => { if (!drag.current?.moved) finish(event, true); },
			onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => { if (event.target === event.currentTarget) finish(event, true); } },
	};
}
